import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import ChartExportAsPNG from "../../../../Utils/ChartExportAsPng";

const MIN_WIDTH = 320;
const MIN_HEIGHT = 260;

const LineChart = ({ data, totalValue, datasetTwo, containerClassName = "", exportRef, selectedCity, year }) => {
  const svgRef = useRef();
  const chartInitialized = useRef(false);
  const [isVisible, setIsVisible] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef();

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = Math.max(MIN_WIDTH, containerRef.current.offsetWidth);
        const height = Math.max(MIN_HEIGHT, Math.min(600, width / 2));
        setDimensions({ width, height });
      }
    };
    handleResize();
    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;
    d3.select("body").selectAll(".tooltip").remove();
    if (!chartInitialized.current) {
      d3.select(svgRef.current).selectAll("*").remove();
      chartInitialized.current = true;
    }

    const margin = { top: 20, right: 30, bottom: 48, left: 50 };
    const { width, height } = dimensions;
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);

    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "auto");


    svg.selectAll("g").remove();
    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);


    const axisFontSize = Math.round(
      Math.max(12, Math.min(16, chartWidth / 40))
    );

    // X and Y scales
    const x = d3
      .scalePoint()
      .domain(data.map((d) => d.year))
      .range([0, chartWidth]);

    const maxPercent = isVisible ? (d3.max(data, (d) => d.value) / totalValue) * 100 : 100;
    const y = d3
      .scaleLinear()
      .domain([0, maxPercent])
      .range([chartHeight, 0])
      .nice();

    // X Grid
    g.append("g")
      .attr("class", "grid x-grid")
      .attr("transform", `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(x).tickSize(-chartHeight).tickFormat("").ticks(data.length))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) =>
        g
          .selectAll("line")
          .attr("stroke", "#e0e0e0")
          .attr("shape-rendering", "crispEdges")
      );

    // Y Grid
    g.append("g")
      .attr("class", "grid y-grid")
      .call(d3.axisLeft(y).tickSize(-chartWidth).tickFormat(""))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) =>
        g
          .selectAll("line")
          .attr("stroke", "#e0e0e0")

          .attr("shape-rendering", "crispEdges")
      );


    const years = data.map(d => d.year);

    let interval = 2

    const filteredTicks = years.filter((year, i) => {
      if (width < 800) {
        return i % interval === 0;
      }

      return year
    });

    // X Axis
    g.append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${chartHeight})`)
      .call(
        d3.axisBottom(x)
          .tickValues(filteredTicks)
          .tickFormat(d => d.toString())
          .tickSizeOuter(0)
      )
      .call(g =>
        g.selectAll(".tick line")
          .attr("stroke", "#e0e0e0")

      )
      .call(g => g.select(".domain").remove())
      .selectAll("text")
      .attr("text-anchor", filteredTicks.length > 10 ? "end" : "middle")
      .attr("transform", filteredTicks.length > 10 ? "rotate(-40)" : null)
      .attr("dx", filteredTicks.length > 10 ? "-0.5em" : "0")
      .attr("dy", filteredTicks.length > 10 ? "0.45em" : "0.71em")
      .attr("fill", "gray")
      .style("font-size", `${axisFontSize}px`);



    // Y Axis
    g.append("g")
      .attr("class", "y-axis")
      .call(
        d3
          .axisLeft(y)
          .ticks(Math.max(5, Math.min(chartHeight / 50)))
          .tickFormat((d) => `${d}%`)
      )
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g
          .selectAll(".tick line")
          .attr("stroke", "#e0e0e0")

      )
      .selectAll("text")
      .attr("fill", "gray")
      .style("font-size", `${axisFontSize}px`);

    // Tooltip
    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("color", "#fff")
      .style("background", "rgba(0, 0, 0, 0.7)")
      .style("border", "1px solid #ddd")
      .style("border-radius", "4px")
      .style("padding", "8px")
      .style("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
      .style("font-size", `${Math.max(12, axisFontSize)}px`)
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("z-index", 1000);

    // Area
    const area = d3
      .area()
      .x((d) => x(d.year))
      .y0(chartHeight)
      .y1((d) => y((d.value / totalValue) * 100))
      .curve(d3.curveMonotoneX);

    g.append("path")
      .datum(data)
      .attr("fill", "#d9d9d9")
      .attr("class", "area")
      .attr("opacity", 0)
      .attr(
        "d",
        d3
          .area()
          .x((d) => x(d.year))
          .y0(chartHeight)
          .y1(chartHeight)
          .curve(d3.curveMonotoneX)
      )
      .transition()
      .duration(500)

      .attr("d", area)
      .attr("opacity", isVisible ? 0.7 : 0);

    // Line
    const line = d3
      .line()
      .x((d) => x(d.year))
      .y((d) => y((d.value / totalValue) * 100))
      .curve(d3.curveMonotoneX);

    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("class", "line")
      .attr("stroke", "#2c5282")
      .attr("stroke-width", 2.5)

      .attr(
        "d",
        d3
          .line()
          .x((d) => x(d.year))
          .y(() => chartHeight)
          .curve(d3.curveMonotoneX)
      )
      .transition()
      .duration(500)
      .attr("opacity", 1)
      .attr("d", line)
      .attr("opacity", isVisible ? 1 : 0);

    // Dots
    g.selectAll(".dot")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "dot")
      .attr("cx", (d) => x(d.year))
      .attr("cy", chartHeight)
      .attr("r", 3.5)
      .attr("fill", "none")
      .attr("stroke", "#2c5282")
      .attr("stroke-width", 2)
      .attr("opacity", 0)
      .on("mouseover", (event, d) => {
        tooltip.html(
          `<strong>Year:</strong> ${d.year}<br/><strong>Value:</strong> ${(
            (d.value / totalValue) *
            100
          ).toFixed(2)}%`
        );
        tooltip
          .style(
            "left",
            `${Math.min(event.pageX + 10, window.innerWidth - 150)}px`
          )
          .style("top", `${event.pageY - 40}px`)
          .transition()
          .duration(200)
          .style("opacity", 1);
      })
      .on("mousemove", (event) => {
        tooltip
          .style(
            "left",
            `${Math.min(event.pageX + 10, window.innerWidth - 150)}px`
          )
          .style("top", `${event.pageY - 40}px`);
      })
      .on("mouseout", () => {
        tooltip.transition().duration(300).style("opacity", 0);
      })
      .transition()
      .duration(500)
      .attr("cy", (d) => y((d.value / totalValue) * 100))
      .attr("opacity", isVisible ? 1 : 0);

    return () => {
      d3.select("body").selectAll(".tooltip").remove();
    };
  }, [data, isVisible, dimensions, totalValue]);

  return (
    <div>
      <div
        className={`flex justify-center ${containerClassName}`}
        ref={containerRef}
        style={{
          width: "100%",
          // minWidth: `${MIN_WIDTH}px`,
          // maxWidth: "100vw",
          // margin: "0 auto",
        }}
        id="line-chart-svg"
      >
        <svg ref={svgRef}></svg>
      </div>
      <div
        className="flex justify-center items-center gap-3 mt-2"

      >
        <div className="flex gap-2 items-center" onClick={() => setIsVisible((v) => !v)}>
          <span
            className="inline-block"
            style={{
              width: 40,
              height: 18,
              background: "#E5E5E5",
              border: "3px solid #2c5282",
              borderRadius: 3,
              display: "inline-block",
            }}
          ></span>
          <button
            style={{
              textDecoration: isVisible ? "none" : "line-through",
              color: "#6B7280",
              fontSize: "clamp(14px, 2vw, 18px)",
              margin: 0,
            }}
          >
            {datasetTwo?.name}
          </button>
        </div>

      </div>

     <ChartExportAsPNG
        ref={exportRef}
        targetId="line-chart-svg"
        targetWidth={600}
        fileNamePrefix={datasetTwo?.name + '- Line Chart ' + selectedCity?.name}
        chartData={data}
        chartType="bar"
        imageTitle={datasetTwo?.name + ' - Line Chart'}
        imageSource={`${selectedCity?.name} ${(selectedCity?.type).charAt(0).toUpperCase() + (selectedCity.type).slice(1).toLowerCase()} - ${year}`}
        tableFont="16px Arial"
        titleFont="bold 20px Arial"
        sourceFont="italic 18px Arial"
        LegendFirstColumn={datasetTwo?.name}
        showColorBlock={false}
      />
    </div>
  );
};

export default LineChart;
